import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProviders, useAuth } from "@/context/AppContext";
import { captureUtm } from "@/lib/draft";
import PublicLayout from "@/components/PublicLayout";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import ComoFunciona from "@/pages/ComoFunciona";
import Areas from "@/pages/Areas";
import Empresas from "@/pages/Empresas";
import Instalar from "@/pages/Instalar";
import { Privacidade, Termos } from "@/pages/Legal";
import Solicitar from "@/pages/Solicitar";
import Entrar from "@/pages/Entrar";
import RedefinirSenha from "@/pages/RedefinirSenha";
import MinhasSolicitacoes from "@/pages/MinhasSolicitacoes";
import SolicitacaoDetalhe from "@/pages/SolicitacaoDetalhe";
import MinhaConta from "@/pages/MinhaConta";
import Advogado from "@/pages/Advogado";

const AdminLogin = lazy(() => import("@/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/admin/AdminDashboard"));
const AdminRequests = lazy(() => import("@/admin/AdminRequests"));
const AdminKanban = lazy(() => import("@/admin/AdminKanban"));
const AdminRequestDetail = lazy(() => import("@/admin/AdminRequestDetail"));
const AdminContacts = lazy(() => import("@/admin/AdminContacts"));
const AdminContactDetail = lazy(() => import("@/admin/AdminContactDetail"));
const AdminDeals = lazy(() => import("@/admin/AdminDeals"));
const AdminSettings = lazy(() => import("@/admin/AdminSettings"));
const AdminExpansion = lazy(() => import("@/admin/AdminExpansion"));
const AdminAudit = lazy(() => import("@/admin/AdminAudit"));

export const Loading = () => (
  <div className="flex min-h-[50vh] items-center justify-center" data-testid="loading">
    <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#EDF5FF] border-t-[#1465C8]" />
  </div>
);

export function RequireUser({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to={`/entrar?next=${encodeURIComponent(loc.pathname)}`} replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user || user.role !== "admin" || !user.mfa) return <Navigate to="/admin/login" replace />;
  return children;
}

function Router() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/como-funciona" element={<ComoFunciona />} />
          <Route path="/areas-juridicas" element={<Areas />} />
          <Route path="/empresas" element={<Empresas />} />
          <Route path="/empresas/consultoria-recorrente" element={<Empresas mode="recorrente" />} />
          <Route path="/empresas/solicitacao-pontual" element={<Empresas mode="pontual" />} />
          <Route path="/instalar" element={<Instalar />} />
          <Route path="/privacidade" element={<Privacidade />} />
          <Route path="/termos" element={<Termos />} />
          <Route path="/solicitar" element={<Solicitar />} />
          <Route path="/entrar" element={<Entrar />} />
          <Route path="/redefinir-senha" element={<RedefinirSenha />} />
          <Route path="/minhas-solicitacoes" element={<RequireUser><MinhasSolicitacoes /></RequireUser>} />
          <Route path="/minhas-solicitacoes/:id" element={<RequireUser><SolicitacaoDetalhe /></RequireUser>} />
          <Route path="/minha-conta" element={<RequireUser><MinhaConta /></RequireUser>} />
          <Route path="/advogado" element={<Advogado />} />
        </Route>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
          <Route index element={<AdminDashboard />} />
          <Route path="solicitacoes" element={<AdminRequests />} />
          <Route path="solicitacoes/:id" element={<AdminRequestDetail />} />
          <Route path="funil" element={<AdminKanban />} />
          <Route path="contatos" element={<AdminContacts />} />
          <Route path="contatos/:id" element={<AdminContactDetail />} />
          <Route path="propostas" element={<AdminDeals />} />
          <Route path="configuracoes" element={<AdminSettings />} />
          <Route path="expansao" element={<AdminExpansion />} />
          <Route path="auditoria" element={<AdminAudit />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  useEffect(() => {
    captureUtm();
  }, []);
  return (
    <AppProviders>
      <BrowserRouter>
        <Router />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AppProviders>
  );
}
